/**
 * Datumberoende fakta om skolan på ett ställe, så att texter inte blir gamla
 * vid årsskiftet. Rena funktioner – inget datalager, ingen nätverkstrafik.
 */

/** Året skolan startade. */
export const FOUNDED_YEAR = 2023;

/** Månad (1-indexerad) då ett nytt läsår räknas som påbörjat: 1 september. */
const SCHOOL_YEAR_START_MONTH = 9;

/** Skolans tidszon – läsåret ska aldrig bero på var servern står. */
const SCHOOL_TIME_ZONE = "Europe/Madrid";

/**
 * Senast bekräftade läsår (angivet med startåret). Spärr så att siffran inte
 * kan glida iväg om systemtiden ligger fel. Höj den när nytt läsår startar.
 */
export const MAX_KNOWN_SCHOOL_YEAR_START = 2026;

function now(date?: Date): Date {
  return date ?? new Date();
}

/** År och månad i skolans tidszon. */
function schoolParts(date?: Date): { year: number; month: number } {
  const d = now(date);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SCHOOL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(d);
  const year = Number(parts.find((p) => p.type === "year")?.value ?? d.getFullYear());
  const month = Number(parts.find((p) => p.type === "month")?.value ?? d.getMonth() + 1);
  return { year, month };
}

/** Läsåret som pågår just nu, angivet med startåret (t.ex. 2025 för 2025/2026). */
export function currentSchoolYearStart(date?: Date): number {
  // Utan ett uttryckligt datum använder vi det senast bekräftade läsåret.
  // Server och webbläsare kan annars hydrera vid olika klockslag/tidszoner och
  // skriva olika text i samma HTML. Det bekräftade året höjs centralt ovan.
  if (!date) return MAX_KNOWN_SCHOOL_YEAR_START;
  const { year, month } = schoolParts(date);
  const started = month >= SCHOOL_YEAR_START_MONTH ? year : year - 1;
  return Math.min(Math.max(started, FOUNDED_YEAR), MAX_KNOWN_SCHOOL_YEAR_START);
}

/** Läsåret som text, t.ex. "2025/2026". */
export function currentSchoolYearLabel(date?: Date): string {
  const start = currentSchoolYearStart(date);
  return `${start}/${start + 1}`;
}

/** Hur många läsår skolan har bedrivit verksamhet, inklusive det pågående. */
export function schoolYearsSince(date?: Date): number {
  return Math.max(1, currentSchoolYearStart(date) - FOUNDED_YEAR + 1);
}

/** Antal hela kalenderår sedan starten. */
export function yearsSinceFounded(date?: Date): number {
  return Math.max(0, schoolParts(date).year - FOUNDED_YEAR);
}

type Locale = "sv" | "en" | "es";

/** "Skola sedan 2023 – nu på vårt fjärde läsår" och motsvarande på en/es. */
export function foundedLabel(locale: Locale, date?: Date): string {
  const years = schoolYearsSince(date);
  if (locale === "en") {
    return `A school since ${FOUNDED_YEAR} – now in our ${ordinalEn(years)} school year`;
  }
  if (locale === "es") {
    return `Colegio desde ${FOUNDED_YEAR} – ya en nuestro ${years}.º curso escolar`;
  }
  return `Skola sedan ${FOUNDED_YEAR} – nu på vårt ${ordinalSv(years)} läsår`;
}

function ordinalEn(n: number): string {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

const SV_ORDINALS = [
  "första",
  "andra",
  "tredje",
  "fjärde",
  "femte",
  "sjätte",
  "sjunde",
  "åttonde",
  "nionde",
  "tionde",
];

function ordinalSv(n: number): string {
  return SV_ORDINALS[n - 1] ?? `${n}:e`;
}

/** Antal hela kalenderår sedan ett startår, t.ex. rektor sedan 2009. */
export function yearsSince(startYear: number, date?: Date): number {
  return Math.max(0, schoolParts(date).year - startYear);
}

/**
 * Räknar om en text som skrevs ett visst år ("16 år" år 2025) till startåret,
 * så att siffran därefter räknas upp automatiskt.
 */
export function startYearFrom(yearsAtBase: number, baseYear: number): number {
  return baseYear - yearsAtBase;
}
